import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'libs/styled';
import { ProductCard, type ProductCardProduct } from 'components/product/ProductCard';
import { GRID_GAP_PX } from 'config/branding/grid';
import { columnWidthPx, imageHeightFromSpanWidthPx, spanWidthPx } from 'Utils/gridLayoutPx';
import { isWideProduct } from 'Utils/isWideProduct';

const GRID_COLS = 24;
/** Matches Figma inactive narrow card span (same basis as carousel inactive narrow). */
const STATIC_NARROW_SPAN_COLS = 4;
const STATIC_WIDE_SPAN_COLS = 8;

const GridRoot = styled('div')`
    display: grid;
    grid-template-columns: repeat(24, minmax(0, 1fr));
    gap: var(--grid-gap);
    min-width: 0;
`;

export const sumProductSliderStaticGridSpans = (products: ProductCardProduct[]): number =>
    products.reduce(
        (sum, product) => sum + (isWideProduct(product) ? STATIC_WIDE_SPAN_COLS : STATIC_NARROW_SPAN_COLS),
        0,
    );

export const productSliderFitsStaticGrid = (products: ProductCardProduct[]): boolean =>
    sumProductSliderStaticGridSpans(products) <= GRID_COLS;

const spanColsForProduct = (product: ProductCardProduct): number =>
    isWideProduct(product) ? STATIC_WIDE_SPAN_COLS : STATIC_NARROW_SPAN_COLS;

export interface ProductSliderStaticGridProps {
    products: ProductCardProduct[];
}

/** Desktop product row: 24-col CSS Grid, no bleed (parent supplies --grid-padding). */
const ProductSliderStaticGrid: React.FC<ProductSliderStaticGridProps> = ({ products }) => {
    const gridRef = useRef<HTMLDivElement | null>(null);
    const [sharedImageHeightPx, setSharedImageHeightPx] = useState<number | null>(null);

    /**
     * useLayoutEffect (not useEffect): without `--product-image-height` on first paint, ProductCard image
     * areas use `height: auto` with only absolutely positioned children → near-zero height, footer jumps
     * up, then ResizeObserver in useEffect ran after paint and fixed it (visible when switching slider → grid).
     */
    useLayoutEffect(() => {
        const node = gridRef.current;
        if (!node || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const updateFromWidth = (widthPx: number) => {
            const colW = columnWidthPx(widthPx, GRID_GAP_PX, GRID_COLS);
            if (colW <= 0) {
                setSharedImageHeightPx(null);
                return;
            }
            const narrowSpanWidthPx = spanWidthPx(STATIC_NARROW_SPAN_COLS, colW, GRID_GAP_PX);
            setSharedImageHeightPx(imageHeightFromSpanWidthPx(narrowSpanWidthPx));
        };

        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                updateFromWidth(entry.contentRect.width);
            }
        });
        ro.observe(node);
        updateFromWidth(node.getBoundingClientRect().width);

        return () => {
            ro.disconnect();
        };
    }, [products.length]);

    if (products.length === 0) {
        return null;
    }

    const gridStyle =
        sharedImageHeightPx != null
            ? ({ '--product-image-height': `${sharedImageHeightPx}px` } as React.CSSProperties)
            : undefined;

    return (
        <GridRoot ref={gridRef} style={gridStyle}>
            {products.map((product, index) => (
                <ProductCard
                    key={product.objectID || String(index)}
                    product={product}
                    gridColumn={`span ${spanColsForProduct(product)}`}
                />
            ))}
        </GridRoot>
    );
};

export default ProductSliderStaticGrid;
